import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Root as DropdownMenuRoot,
  Trigger,
  Portal,
  Group,
  Item,
} from '@radix-ui/react-dropdown-menu';
import { ClickAwayListener } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';
import usePrevious from '../../../lib/usePrevious';
import { MenuAction } from './types';
import MenuContent from '../../DropdownMenu/components/MenuContent';
import MenuSeparator from '../../DropdownMenu/components/MenuSeparator';
import theme from '../../../theme';

export interface ProjectActionMenuProps
  extends Omit<ProjectActionMenuContentProps, 'setOpen'> {
  id: string;
  ariaLabel: string;
  icon?: AvailableIconTypes;
  disablePortal?: boolean;
  /**
   * True if used in a modal or absolute positioned element (toolbar)
   * Helps with controlling escape keydown
   */
  usedInModal?: boolean;
  /**
   * True if used in Avatar Options
   * Allows menu to close in order to see loader implemented in performance style dropdown in avatar card
   */
  disableDelayedMenuClose?: boolean;
}

interface ProjectActionMenuContentProps {
  primaryActions: Array<MenuAction>;
  destructiveActions: Array<MenuAction>;
  isPortaled?: boolean;
  isLoading?: boolean;
  onEscapeKeyDown?: () => void;
}

const ProjectActionMenuContent = React.forwardRef<
  HTMLDivElement,
  ProjectActionMenuContentProps
>((props, ref) => {
  const {
    primaryActions,
    destructiveActions,
    isPortaled,
    isLoading,
    onEscapeKeyDown,
  } = props;
  const handleMenuKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && onEscapeKeyDown) {
      e.stopPropagation();
      onEscapeKeyDown();
    }
  };
  return (
    <MenuContent
      data-active-section-portal={isPortaled || undefined}
      ref={ref}
      align="start"
      collisionPadding={16}
      sideOffset={4}
      style={{ zIndex: theme.zIndex.tooltip }}
      onKeyDown={handleMenuKeydown}
    >
      <Group>
        {primaryActions
          .filter(action => action.show)
          .map(action => {
            if (!action.options || action.options.length < 1)
              return (
                <Item
                  key={action.label}
                  onSelect={action.onSelect}
                  disabled={action.disabled}
                >
                  <Typography variant="pSm">{action.label}</Typography>
                </Item>
              );
            return null;
          })}
        {destructiveActions.filter(action => action.show).length > 0 && (
          <MenuSeparator />
        )}
        {destructiveActions
          .filter(action => action.show)
          .map(action => (
            <Item key={action.label} onSelect={action.onSelect}>
              <Typography variant="pSm" color="error">
                {action.label}
              </Typography>
            </Item>
          ))}
      </Group>
    </MenuContent>
  );
});

/**
 * Presentational component for dropdown menu for project card actions
 * The only state handled here is whether the menu is open/closed and
 * if to show disabled/loading states.
 */
const ProjectActionMenu = (props: ProjectActionMenuProps) => {
  const {
    id,
    ariaLabel,
    isLoading,
    icon = 'moreHoriz',
    disablePortal = false,
    usedInModal = false,
    disableDelayedMenuClose,
    ...restActions
  } = props;
  const [open, setOpen] = useState(false);

  const buttonId = useMemo(() => `${id}-button`, [id]);

  const { classes: dropdownMenuClasses, cx } = useStyles();

  const hideFocusStyle = useCallback(
    (hideFocus: boolean) => {
      const triggerButton = document.getElementById(buttonId);
      if (triggerButton) {
        const dataClassName = 'data-hide-focus';
        if (hideFocus) {
          triggerButton.setAttribute(dataClassName, '');
        } else {
          triggerButton.removeAttribute(dataClassName);
        }
      }
    },
    [buttonId]
  );

  /**
   * Let menu remain open while loading is happening,
   * once action complete, close menu
   */
  const prevOpen = usePrevious(open);
  useEffect(() => {
    if (!disableDelayedMenuClose) {
      if (isLoading && !open) {
        setOpen(true);
      }
    }
    if (!isLoading && prevOpen) {
      setOpen(false);
    }
  }, [disableDelayedMenuClose, isLoading, open, prevOpen]);

  // Only if used in modal, override escape keydown callback
  const handleEscapeKeyDown = useMemo(() => {
    if (!usedInModal) return undefined;
    return () => setOpen(false);
  }, [usedInModal]);

  const handleKeyboardMouseClickFocus = useCallback(
    (hide: boolean) => {
      hideFocusStyle(hide);
    },
    [hideFocusStyle]
  );

  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen}>
      <ClickAwayListener
        onClickAway={() => handleKeyboardMouseClickFocus(true)}
      >
        <Trigger asChild>
          <Button
            variant="icon"
            id={buttonId}
            color="default"
            aria-label={ariaLabel}
            data-e2e="action-list"
            className={cx(dropdownMenuClasses?.iconButton, { open })}
            onKeyUpCapture={() => handleKeyboardMouseClickFocus(false)}
          >
            <Icon
              color="secondary"
              type={icon}
              className={cx(dropdownMenuClasses?.icon)}
            />
          </Button>
        </Trigger>
      </ClickAwayListener>
      {disablePortal ? (
        <ProjectActionMenuContent
          onEscapeKeyDown={handleEscapeKeyDown}
          isLoading={isLoading}
          {...restActions}
        />
      ) : (
        <Portal>
          <ProjectActionMenuContent
            isPortaled
            isLoading={isLoading}
            {...restActions}
          />
        </Portal>
      )}
    </DropdownMenuRoot>
  );
};

const useStyles = makeStyles()(() => ({
  iconButton: {
    height: '28px',
    width: '28px',
    '&:hover': {
      backgroundColor: '#FFFFFF15',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_200}`,
      outlineOffset: 2,
      backgroundColor: '#FFFFFF15',
    },
    '&.open': {
      color: palette.mint_200,
      backgroundColor: '#FFFFFF15',
      borderRadius: theme.spacing(0.5),
    },
  },
  icon: {
    height: '20px',
    width: '20px',
  },
}));

export default ProjectActionMenu;
